import React, { FC, ReactNode, useId, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import {
    ConfirmDialog as PrimeConfirmDialog,
    ConfirmDialogOptions,
    ConfirmDialogProps,
    confirmDialog,
} from 'primereact/confirmdialog'
import { Checkbox } from 'primereact/checkbox'
import { Button } from 'primereact/button'

import useText from '../../hooks/useText'
import { defaultDialogProps } from '../Dialog'

type ConfirmVariant = 'normal' | 'danger'

type ConfirmConfig = {
    header: string
    message: ReactNode
    variant?: ConfirmVariant
    checkboxLabel?: ReactNode
}

type ConfirmationFooterProps = {
    options: ConfirmDialogOptions
    checkboxLabel: ReactNode
}

const ConfirmationFooter: FC<ConfirmationFooterProps> = ({ options, checkboxLabel }) => {
    const text = useText()
    const checkboxId = useId()
    const [checked, setChecked] = useState(false)

    return (
        <div className="flex flex-column gap-3">
            <div className="flex align-items-center gap-2">
                <Checkbox inputId={checkboxId} checked={checked} onChange={(event) => setChecked(!!event.checked)} />
                <label htmlFor={checkboxId} className="mb-0">
                    {checkboxLabel}
                </label>
            </div>
            <div className="flex justify-content-end gap-2">
                <Button
                    type="button"
                    label={text('lblNo')}
                    text
                    autoFocus={options.props.defaultFocus === 'reject'}
                    onClick={() => options.reject()}
                />
                <Button
                    type="button"
                    label={text('btnYes')}
                    className={options.acceptClassName}
                    autoFocus={options.props.defaultFocus === 'accept'}
                    disabled={!checked}
                    onClick={() => options.accept()}
                />
            </div>
        </div>
    )
}

export const confirm = ({ header, message, variant = 'normal', checkboxLabel }: ConfirmConfig): Promise<boolean> =>
    new Promise((resolve) => {
        confirmDialog({
            message,
            header,
            icon: variant === 'danger' ? 'pi pi-exclamation-triangle' : undefined,
            acceptClassName: variant === 'danger' ? 'p-button-danger' : '',
            defaultFocus: variant === 'danger' ? 'reject' : 'accept',
            accept: () => resolve(true),
            reject: () => resolve(false),
            onHide: () => resolve(false),
            footer:
                checkboxLabel !== undefined
                    ? (options) => <ConfirmationFooter options={options} checkboxLabel={checkboxLabel} />
                    : undefined,
        })
    })

type Props = {
    className?: ClassName
    testId?: string
} & ConfirmDialogProps

export const ConfirmDialog: FC<Props> = ({ testId = 'confirm-dialog', className, style, ...props }) => (
    <PrimeConfirmDialog
        {...defaultDialogProps}
        className={cn(className)}
        data-testid={testId}
        style={{ ...defaultDialogProps.style, ...style }}
        resizable={false}
        {...props}
    />
)
